'use client';

import { FC, useEffect, useMemo, useState } from 'react';
import { Clock } from 'lucide-react';
import { useTranslations } from 'next-intl';
import Select from 'react-select';
import { useRouter, useSearchParams } from 'next/navigation';
import { ApplicableSubscriptionsReadOnly } from 'hotdesk_typescript';
import { useAppSelector } from '@/hotdesk-ui/hooks/hook';
import { RootState } from '@/hotdesk-ui/store/store';
import { currencyConvert } from '@/hotdesk-ui/helpers/utils';
import { NumberFormatService } from '@/hotdesk-ui/services/number-format.service';
import { firebaseAnalytics, fetchRemoteConfig } from '@/hotdesk-ui/common-classes/firebase.service';
import { RemoteConfigKeys } from '@/hotdesk-ui/enum/remote-keys';
import { tracking } from '@/hotdesk-ui/services/tracking.service';
import { getProviderTrackingProps } from '@/hotdesk-ui/utils/provider';
import { getSubscriptionPromoDetails, resolveInitialPriceUnit, PRICE_UNIT_ORDER, getMembershipDiscountedUnitPrice } from '@/hotdesk-ui/utils/listingSubscriptionUtils';
import { usePreferredUserSubscriptionIds } from '@/hotdesk-ui/hooks/usePreferredUserSubscriptionIds';
import { getActiveUserSubscriptions } from '@/hotdesk-ui/utils/subscriptionUtils';
import HotdeskButton from '@/hotdesk-ui/components/shared/HotdeskButton';
import ScrollablePriceText from '@/hotdesk-ui/components/shared/ScrollablePriceText';
import WorkScheduleModal from './WorkScheduleModal';
import PlanUnitOption from './PlanUnitOption';
import styles from './BookingSidebar.module.scss';
export interface BookingSidebarProps {
  variant: 'workspace' | 'listing';
  listing?: Record<string, any>;
  applicableSubscriptions?: ApplicableSubscriptionsReadOnly | null;
  onViewListings?: () => void;
  onContactSales?: () => void;
  subscription?: string | null;
  price_unit?: string;
  setPrice_unit?: (value: string) => void;
}
const PRICE_ORDER = [...PRICE_UNIT_ORDER];
const reorderPrices = (priceObj: Record<string, number>): Record<string, number> => {
  const ordered: Record<string, number> = {};
  PRICE_ORDER.forEach(key => {
    if (key in priceObj) ordered[key] = priceObj[key];
  });
  return ordered;
};
const getWorkingHoursToday = (workingHours: Record<string, string> | undefined) => {
  if (!workingHours) return {
    dayLabel: '',
    hours: ''
  };
  const days = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  const dayIndex = new Date().getDay();
  const todayKey = days[dayIndex];
  if (workingHours[todayKey]) {
    return {
      dayLabel: 'today',
      hours: workingHours[todayKey]
    };
  }
  let nextIdx = (dayIndex + 1) % 7;
  while (nextIdx !== dayIndex) {
    const key = days[nextIdx];
    if (workingHours[key]) {
      return {
        dayLabel: nextIdx === (dayIndex + 1) % 7 ? 'tomorrow' : key,
        hours: workingHours[key]
      };
    }
    nextIdx = (nextIdx + 1) % 7;
  }
  return {
    dayLabel: '',
    hours: ''
  };
};
const BookingSidebar: FC<BookingSidebarProps> = ({
  variant,
  listing,
  applicableSubscriptions,
  onViewListings,
  onContactSales,
  subscription,
  price_unit = '',
  setPrice_unit
}) => {
  const t = useTranslations();
  const router = useRouter();
  const searchParams = useSearchParams();
  const frequencyParam = searchParams.get('frequency');
  const currencyList = useAppSelector((state: RootState) => state.currency.currency);
  const defaultCurrency = useAppSelector((state: RootState) => state.currency.defaultCurrency);
  const languageCode = useAppSelector((state: RootState) => state.listing.defaultLanguage);
  const user = useAppSelector((state: RootState) => state.auth.user);
  const preferredIds = usePreferredUserSubscriptionIds();
  const fallbackUserSubs = useMemo(() => getActiveUserSubscriptions(user) as unknown as Record<string, unknown>[], [user]);
  const priceObj = listing?.rules?.price || {};
  const orderedPrices = reorderPrices(priceObj);
  const timeOptions = Object.keys(orderedPrices).map(key => ({
    value: key,
    label: t('workspace-details.' + key)
  }));
  const {
    appliesFor,
    cap: promoCap,
    packageName
  } = useMemo(() => getSubscriptionPromoDetails(applicableSubscriptions, listing?.type, preferredIds, fallbackUserSubs), [applicableSubscriptions, listing?.type, preferredIds, fallbackUserSubs]);
  const resolveSelectedUnit = useMemo(() => {
    return (unit?: string | null) => resolveInitialPriceUnit({
      priceObj: orderedPrices,
      frequencyParam: unit,
      preferredUnit: price_unit || null,
      appliesFor
    });
  }, [orderedPrices, appliesFor, price_unit]);
  const [selectedUnit, setSelectedUnit] = useState(() => resolveSelectedUnit(frequencyParam || price_unit || null));
  const [displayPrice, setDisplayPrice] = useState('');
  const [discountedPrice, setDiscountedPrice] = useState(0);
  const [currencyName, setCurrencyName] = useState('');
  const [remoteWhatsappLink, setRemoteWhatsappLink] = useState<string | null>(null);
  const workingHours = listing?.rules?.workingHours;
  const {
    dayLabel,
    hours
  } = getWorkingHoursToday(workingHours);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [useMembership, setUseMembership] = useState(true);
  const unitHasSubscription = !!subscription;
  useEffect(() => {
    if (unitHasSubscription) {
      setUseMembership(true);
    }
  }, [listing?.id, unitHasSubscription]);
  useEffect(() => {
    setSelectedUnit(resolveInitialPriceUnit({
      priceObj: orderedPrices,
      frequencyParam,
      appliesFor
    }));
    // Reset only when navigating to another listing or URL frequency changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listing?.id, frequencyParam]);
  useEffect(() => {
    const loadRemoteWhatsappLink = async () => {
      try {
        const link = await fetchRemoteConfig(RemoteConfigKeys.WEB_WHATSAPP_LINK, 'string');
        setRemoteWhatsappLink(link as string);
      } catch (error) {
        console.error('Failed to fetch remote WhatsApp link:', error);
        setRemoteWhatsappLink(null);
      }
    };
    loadRemoteWhatsappLink();
  }, []);
  useEffect(() => {
    if (!defaultCurrency?.code || !orderedPrices[selectedUnit]) return;
    const converted = currencyConvert(currencyList, listing?.rules?.currency as string, defaultCurrency.code, orderedPrices[selectedUnit]);
    let convertedCap: number | null = null;
    if (promoCap != null) {
      convertedCap = currencyConvert(currencyList, listing?.rules?.currency as string, defaultCurrency.code, promoCap);
    }
    const formatter = new NumberFormatService();
    setDisplayPrice(formatter.formatNumberWithCountingLabel(converted, languageCode));
    setCurrencyName(defaultCurrency.name || defaultCurrency.code);
    setDiscountedPrice(getMembershipDiscountedUnitPrice(converted, convertedCap));
  }, [defaultCurrency, currencyList, selectedUnit, listing, promoCap, languageCode, orderedPrices]);
  useEffect(() => {
    setPrice_unit?.(selectedUnit);
  }, [selectedUnit, setPrice_unit]);
  const handleBookNow = () => {
    if (!user) firebaseAnalytics('book_now_clicked_no_login');else firebaseAnalytics('book_now_clicked');
    firebaseAnalytics('web_booking_unit_selected', {
      web_booking_unit: selectedUnit
    });
    if (listing?.id && typeof window !== 'undefined') {
      tracking.onReviewBookingClicked(listing?.workspace?.id ?? '', listing.id, window.location.href, getProviderTrackingProps(listing));
      tracking.onBookingUnitSelected(listing.id, window.location.href, getProviderTrackingProps(listing));
    }
    const params = new URLSearchParams();
    // Only carry membership into booking when the toggle is on.
    if (useMembership && unitHasSubscription) {
      const planForBooking = subscription || packageName || '';
      if (planForBooking) params.set('subscription', planForBooking);
    }
    const qs = params.toString();
    router.push(`/booking/${listing?.id}/${selectedUnit}${qs ? `?${qs}` : ''}`);
  };
  const handleTimeChange = (val: string) => {
    firebaseAnalytics('navigate_plans', {
      current: selectedUnit,
      selected: val
    });
    setSelectedUnit(val);
    setPrice_unit?.(val);
  };
  const whatsappNumber = listing?.workspace?.metaData?.phoneNumber || listing?.metaData?.phoneNumber;
  const whatsappUrl = remoteWhatsappLink || (whatsappNumber ? `https://wa.me/${whatsappNumber.replace(/[^0-9]/g, '')}` : null);
  const membershipOn = unitHasSubscription && useMembership;
  const renderMembershipToggle = () => {
    if (!unitHasSubscription) return null;
    return <div className={styles.membershipToggleRow} data-sentry-component="renderMembershipToggle" data-sentry-source-file="BookingSidebar.tsx">
                <span className={styles.membershipToggleLabel}>
                    {t('workspace-details.useMyMembership')}
                </span>
                <div className={styles.membershipToggleControl}>
                    <button type="button" className={styles.membershipToggle} data-on={membershipOn} onClick={() => setUseMembership(prev => !prev)} role="switch" aria-checked={membershipOn} aria-label={t('workspace-details.useMyMembership')}>
                        <span className={styles.membershipToggleKnob} />
                    </button>
                </div>
            </div>;
  };
  const renderPrice = () => {
    if (membershipOn && discountedPrice === 0) {
      return <div className={styles.priceRow}>
                    <ScrollablePriceText title={`${currencyName} 0`}>
                        <span className={styles.priceValue}>
                            {currencyName} 0
                        </span>
                    </ScrollablePriceText>
                    <span className={styles.priceUnit}>
                        / {t('workspace-details.' + selectedUnit)}
                    </span>
                </div>;
    }
    if (membershipOn && discountedPrice > 0) {
      return <div className={`${styles.priceRow} ${styles.priceRowDiscounted}`}>
                    <span className={styles.priceStrikethrough}>
                        {currencyName} {displayPrice}
                    </span>
                    <span className={styles.priceValue}>
                        {discountedPrice.toFixed(2)}
                    </span>
                    <span className={styles.priceUnit}>
                        {currencyName} / {t('workspace-details.' + selectedUnit)}
                    </span>
                </div>;
    }
    if (!displayPrice) return null;
    return <div className={styles.priceRow} data-sentry-component="renderPrice" data-sentry-source-file="BookingSidebar.tsx">
                <ScrollablePriceText title={`${currencyName} ${displayPrice}`} data-sentry-element="ScrollablePriceText" data-sentry-source-file="BookingSidebar.tsx">
                    <span className={styles.priceValue}>
                        {currencyName} {displayPrice}
                    </span>
                </ScrollablePriceText>
                <span className={styles.priceUnit}>  
                    /  {t('workspace-details.' + selectedUnit)}
                </span>
            </div>;
  };
  return <aside className={styles.sidebar} data-sentry-component="BookingSidebar" data-sentry-source-file="BookingSidebar.tsx">
            {dayLabel && hours && <button type="button" className={styles.hoursRow} onClick={() => setScheduleOpen(true)} aria-label={t('workspace-details.workSchedule')}>
                    <Clock size={20} strokeWidth={2} />
                    <span className={styles.hoursText}>
                        {t(dayLabel)} - {hours}
                    </span>
                </button>}

            <WorkScheduleModal open={scheduleOpen} onClose={() => setScheduleOpen(false)} workingHours={workingHours} data-sentry-element="WorkScheduleModal" data-sentry-source-file="BookingSidebar.tsx" />

            <div className={styles.pricingCard}>
                {variant === 'listing' && timeOptions.length > 1 && <div className={styles.selectWrap}>
                        <Select value={timeOptions.find(o => o.value === selectedUnit) ?? timeOptions[0]} options={timeOptions} isSearchable={false} menuPlacement="auto" classNamePrefix="sidebar-select" onChange={opt => opt && handleTimeChange(opt.value)} getOptionLabel={o => o.label} getOptionValue={o => o.value} formatOptionLabel={option => <PlanUnitOption unit={option.value} label={option.label} iconSize={18} />} />
                    </div>}

                {(displayPrice || unitHasSubscription) && <div className={styles.priceDisplay}>
                        {renderMembershipToggle()}
                        {!unitHasSubscription ? <span className={styles.priceLabel}>
                                {t('workspace-details.startingFrom')}
                            </span> : null}
                        {renderPrice()}
                    </div>}

                {variant === 'workspace' ? <HotdeskButton variant="primary" size="md" fullWidth onClick={onViewListings}>
                        {t('workspace-details.viewListings')}
                    </HotdeskButton> : <div className={styles.bookNowWrap}>
                        <HotdeskButton variant="primary" size="md" fullWidth onClick={handleBookNow}>
                            {t('header.book_btn')}
                        </HotdeskButton>
                        <p className={styles.notChargedYet}>{t('workspace-details.notChargedYet')}</p>
                    </div>}
            </div>

            <HotdeskButton variant="secondary" size="md" fullWidth onClick={onContactSales} data-sentry-element="HotdeskButton" data-sentry-source-file="BookingSidebar.tsx">
                {t('workspace-details.contactSales')}
            </HotdeskButton>

            {whatsappUrl ? <HotdeskButton as="link" variant="secondary" size="md" fullWidth href={whatsappUrl} target="_blank" rel="noopener noreferrer" iconLeft={
    // eslint-disable-next-line @next/next/no-img-element
    <img src="/assets/icons/new-whatsapp.svg" alt="WhatsApp" width={20} height={20} />}>
                    {t('workspace-details.chatWhatsApp')}
                </HotdeskButton> : <HotdeskButton variant="secondary" size="md" fullWidth iconLeft={
    // eslint-disable-next-line @next/next/no-img-element
    <img src="/assets/icons/Icon.svg" alt="WhatsApp" width={20} height={20} />}>
                    {t('workspace-details.chatWhatsApp')}
                </HotdeskButton>}
        </aside>;
};
export default BookingSidebar;